import { PATH_DASHBOARD } from '../../../routes/paths';
import { SvgColor } from '../../../components/svg-color';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import DraftSessionBadge from '../../../components/annotations/DraftSessionBadge';
import TaskBadge from '../../../components/TaskBadge';

const getIcon = (name) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} />;

const ICONS = {
  blog: getIcon('ic_blog'),
  cart: getIcon('ic_cart'),
  chat: getIcon('ic_chat'),
  mail: getIcon('ic_mail'),
  user: getIcon('ic_user'),
  kanban: getIcon('ic_kanban'),
  banking: getIcon('ic_banking'),
  job: getIcon('ic_job'),
  invoice: getIcon('ic_file'),
  tables: getIcon('ic_filter'),
  calendar: getIcon('ic_calendar'),
  ecommerce: getIcon('ic_ecommerce'),
  analytics: getIcon('ic_analytics'),
  dashboard: getIcon('ic_dashboard'),
  menuItem: getIcon('ic_menu_item'),
  campaigns: getIcon('ic_campaign'),
  notification: getIcon('ic_notification'),
  task: getIcon('ic_task'),
  campaign: getIcon('ic_campaign'),
  financial: getIcon('ic_financial'),
  qrcode: getIcon('ic_qrcode'),
  facebook: getIcon('ic_facebook'),
  whatsapp: getIcon('ic_whatsapp'),
  sms: getIcon('ic_sms'),
  patientReview: getIcon('ic_label'),

  leadManagement: getIcon('ic_folder'),
};

// Maps TABLE_NAMES values (used as hidden_parent_tables entries) → nav child titles.
// Only entries that map to filterable nav children need to appear here.
const TABLE_NAME_TO_NAV_TITLE = {
  'Newest Leads': 'New',
  'Consultation Booked Leads': 'Booked',
  'Consultation Attended Leads': 'Attended',
  'Treatment Started Leads': 'Started',
  'Failed to attend Leads': 'FTA',
  'Completed Leads': 'Completed',
  'Archived Leads': 'Archived',
  'No Task Assigned': 'No Task Assigned',
  'Aligner Approve Leads': 'Aligner Approve',
};

const getNavConfig = (practiceId, { isSuperAdmin = false, hasMultiplePractices = false, hasTasksV2 = false, hiddenParentTables = [] } = {}) => {
  const hiddenNavTitles = new Set(hiddenParentTables.map((t) => TABLE_NAME_TO_NAV_TITLE[t]).filter(Boolean));

  const activeLeadsChildren = [
    { title: 'NHS', path: PATH_DASHBOARD.tables.nhsLeadTable },
    { title: 'New', path: PATH_DASHBOARD.tables.newLeadTable },
    { title: 'Booked', path: PATH_DASHBOARD.tables.ConsultantBookedTable },
    { title: 'Attended', path: PATH_DASHBOARD.tables.ConsultantAttendedTable },
    { title: 'CA Pipeline (New)', path: PATH_DASHBOARD.tables.ConsultantAttendedV2Table, feature: 'ca-pipeline-v2' },
    { title: 'Started', path: PATH_DASHBOARD.tables.treatmentStartedTable },
    { divider: true },
    { title: 'Aligner Approve', path: PATH_DASHBOARD.tables.alignerApproveTable, feature: 'aligner-approve' },
    ...(hasTasksV2 ? [{ title: 'Attended Checklists', path: PATH_DASHBOARD.tables.attendedChecklistsTable }] : []),
  ].filter((item) => !item.title || !hiddenNavTitles.has(item.title));

  return [
  {
    subheader: 'Patients',
    items: [
      { title: 'Dashboard', path: PATH_DASHBOARD.general.app, icon: ICONS.dashboard },
      {
        title: 'Active Leads',
        path: PATH_DASHBOARD.tables.newLeadTable,
        icon: ICONS.user,
        children: activeLeadsChildren,
      },
      {
        title: 'Lead Management',
        path: PATH_DASHBOARD.tables.archiveLeadTable,
        icon: ICONS.leadManagement,
        children: [
          { title: 'All', path: PATH_DASHBOARD.tables.AllLeadTable },
          { title: 'Archived', path: PATH_DASHBOARD.tables.archiveLeadTable },
          { title: 'Duplicate Leads', path: PATH_DASHBOARD.tables.duplicateLeadTable },
          { title: 'Spam', path: PATH_DASHBOARD.tables.spamLeadTable },
          { divider: true },
          { title: 'No Task Assigned', path: PATH_DASHBOARD.tables.NoTaskLeadsTable },
          { divider: true },
          { title: 'FTA', path: PATH_DASHBOARD.tables.failedToAttendTable },
          { title: 'Completed', path: PATH_DASHBOARD.tables.closedTable },
          { title: 'All Patients', path: PATH_DASHBOARD.tables.allPatients },
          ...(hasMultiplePractices ? [{ divider: true }, { title: 'All Practice Leads', path: PATH_DASHBOARD.tables.InterPractice }] : []),
        ].filter((item) => !item.title || !hiddenNavTitles.has(item.title)),
      },
      { title: 'Dentally', path: PATH_DASHBOARD.tables.dentallyPatients, icon: <Iconify icon="medical-icon:i-dental" width={24} />, feature: 'dentally-integration' },
      { title: 'Tasks', path: hasTasksV2 ? PATH_DASHBOARD.task.v2Root : PATH_DASHBOARD.task.root, icon: ICONS.task, info: (
          hasTasksV2
            ? <Label color="info" variant="filled" sx={{ borderRadius: '10px', fontSize: '10px', height: 18, px: 0.75 }}>Beta</Label>
            : <TaskBadge />
        ) },
      { title: 'Forms', path: PATH_DASHBOARD.forms.root, icon: ICONS.invoice, feature: 'forms-v2', requirePermission: true },
    ],
  },
  {
    subheader: 'Communication',
    items: [
      { title: 'WhatsApp', path: PATH_DASHBOARD.whatsapp.root(practiceId), icon: ICONS.whatsapp, feature: 'whatsapp-messaging' },
      {
        title: 'More',
        path: PATH_DASHBOARD.emails.root,
        icon: <Iconify icon="eva:more-horizontal-fill" width={24} />,
        children: [
          { title: 'Emails', path: PATH_DASHBOARD.emails.root, feature: 'email-inbox' },
          { title: 'Call Logs', path: PATH_DASHBOARD.callLogs.root, feature: 'call-logs' },
          { title: 'Facebook', path: PATH_DASHBOARD.user.facebookComment, feature: 'facebook-integration' },
          { title: 'Instagram', path: PATH_DASHBOARD.instagram.root(practiceId), info: <Label color="warning" variant="filled" sx={{ borderRadius: '10px', fontSize: '10px', height: 18, px: 0.75 }}>Soon</Label>, feature: 'instagram-integration' },
        ],
      },
    ],
  },
  {
    subheader: 'Business',
    items: [
      {
        title: 'Reporting',
        path: PATH_DASHBOARD.analyticsReports.overview,
        icon: ICONS.analytics,
        info: <Label color="primary" variant="filled" sx={{ borderRadius: '10px', fontSize: '10px', height: 18, px: 0.75 }}>Beta v 10</Label>,
        feature: 'analytics-reports',
      },
      { title: 'Reviews & Feedback', path: PATH_DASHBOARD.manageBusiness, icon: <Iconify icon="logos:google-icon" width={18} />, feature: 'google-reviews' },
      // Marketing Tracker + Campaign Config moved into Reporting → More
      // dropdown as super-admin-only entries (see AnalyticsReportsTabs.MORE_TABS).
      {
        title: 'More',
        path: PATH_DASHBOARD.financialTracker,
        icon: <Iconify icon="eva:more-horizontal-fill" width={24} />,
        children: [
          // Legacy Analytics surface — super-admin-only entry point until
          // the new analytics-reports tabs fully supersede it.
          ...(isSuperAdmin ? [{ title: 'Analytics', path: PATH_DASHBOARD.analytics.reports }] : []),
          { title: 'Financial Tracker', path: PATH_DASHBOARD.financialTracker, feature: 'financial-tracker' },
          { title: 'QR Codes', path: PATH_DASHBOARD.qrCodeGenerator, feature: 'qr-code-generator' },
          { title: 'Survey', path: PATH_DASHBOARD.survey.root },
        ],
      },
    ],
  },
  {
    subheader: 'Marketing Tools',
    items: [
      // Marketing Calendar — collapses the previously-nested
      // Calendar + Marketing Brief children into a single sidebar item.
      // Each page renders <MarketingCalendarTabs /> at the top for navigation.
      // Gated by the same `email-campaigns` feature as Campaigns Manager —
      // the old structure inherited this gate via the parent item; keep
      // the gate explicit on the flat item so behaviour matches.
      {
        title: 'Marketing Calendar',
        path: PATH_DASHBOARD.planner,
        icon: <Iconify icon="solar:calendar-bold-duotone" width={24} />,
        feature: 'email-campaigns',
      },
      // Campaigns Manager — collapses the previously-nested 6 children
      // (Campaigns / Audience Manager / Templates / Reports / Triggers /
      // Smart Segments) into a single sidebar item. Each page renders
      // <CampaignsManagerTabs /> at the top for navigation.
      {
        title: 'Campaigns Manager',
        path: PATH_DASHBOARD.campaign.campaign,
        icon: ICONS.campaigns,
        feature: 'email-campaigns',
      },
      { title: 'Google Posts', path: PATH_DASHBOARD.googlePosts, icon: <Iconify icon="logos:google-icon" width={18} />, feature: 'google-reviews' },
    ],
  },
  // STORAGE section temporarily hidden from the sidebar — the page
  // and route are intentionally retained so existing bookmarks and
  // deep-links keep working; only the nav entry is suppressed. Restore
  // by un-commenting this block once Storage is ready to surface again.
  // {
  //   subheader: 'Storage',
  //   items: [
  //     { title: 'Storage', path: PATH_DASHBOARD.storage.root, icon: <Iconify icon="solar:cloud-storage-bold-duotone" width={24} /> },
  //   ],
  // },
  {
    subheader: 'More Tools',
    items: [
      { title: 'Website Annotation', path: PATH_DASHBOARD.websiteAnnotation, icon: <Iconify icon="mdi:comment-edit-outline" width={24} />, info: <DraftSessionBadge />, feature: 'website-annotation' },
    ],
  },
];
};

export default getNavConfig;
